import { FC, useState } from 'react';
import { Plus, DollarSign, CheckCircle, Archive, TrendingUp } from 'lucide-react';
import { MainLayout } from 'pages/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CityFeeSheet } from 'components/city-fees/CityFeeSheet';
import { CityFeesDataTable } from 'components/city-fees/CityFeesDataTable';
import { useCityFees } from 'src/hooks/useCityFees';
import { safeMoneyFromCents } from '@libs/services/money';

export const CityFeesDashboard: FC = () => {
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const { stats, isLoadingStats } = useCityFees();

  return (
    <MainLayout>
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:justify-between sm:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">City Fees</h1>
          <p className="text-slate-500 mt-1 dark:text-slate-400">
            Manage city-specific fees for your organization
          </p>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => setIsCreateSheetOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add City Fee
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total Fees
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingStats ? (
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <div className="text-2xl font-semibold">{stats?.total ?? 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Active Fees
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingStats ? (
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <div className="text-2xl font-semibold text-green-600">{stats?.active ?? 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Archived Fees
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingStats ? (
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <div className="text-2xl font-semibold text-slate-600">{stats?.archived ?? 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Average Fee
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingStats ? (
              <div className="h-8 w-20 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <div className="text-2xl font-semibold text-text-primary">
                {safeMoneyFromCents(stats?.averageFee ?? 0).toFormat()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <CityFeesDataTable />

      {/* Create Sheet */}
      <CityFeeSheet open={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen} cityFee={null} />
    </MainLayout>
  );
};
